import { InputDatePicker } from "@/components/elements/InputDatePicker";
import { dayjs } from "@/utils/dayjs";
import { Dayjs } from "dayjs";
import { Box, FormHelperText } from "@mui/material";
import { memo, useCallback } from "react";
import { TEditableNoteField } from "@/features/management-ledger/components/LedgerNoteEditableCell";
import { formatBanPeriodDisplay, parseBanPeriod, validateBanPeriod } from "@/features/management-ledger/utils/job-change-ban-period";

type TProps = {
  row: {
    caseMatchId: number | null;
  };
  value: string;
  isEditing: boolean;
  onChange: (caseMatchId: number, field: TEditableNoteField, value: string) => void;
};

export const LedgerJobChangeBanPeriodCell = memo(
  ({ row, value, isEditing, onChange }: TProps) => {
    const caseMatchId = row.caseMatchId;

    const handleChange = useCallback(
      (next: Dayjs | null) => {
        if (caseMatchId == null) return;
        onChange(caseMatchId, "permanentEmploymentNote", next ? next.format("YYYY-MM-DD") : "");
      },
      [caseMatchId, onChange],
    );

    if (!isEditing || caseMatchId == null) {
      return (
        <Box component="span" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
          {formatBanPeriodDisplay(value)}
        </Box>
      );
    }

    const date = parseBanPeriod(value);
    const errorMessage = validateBanPeriod(value) || undefined;

    return (
      <Box sx={{ display: "flex", flexDirection: "column", minHeight: 33, position: "relative", width: "100%" }}>
        <InputDatePicker nonControl value={date ? dayjs(date) : null} error={Boolean(errorMessage)} onChange={handleChange} disableHelperText />
        {Boolean(errorMessage) && (
          <FormHelperText
            error
            sx={{
              position: "absolute",
              top: "100%",
              left: 0,
              right: 0,
              marginTop: 0,
              marginLeft: "4px",
              lineHeight: 1.2,
              whiteSpace: "pre-line",
              zIndex: 1,
              backgroundColor: "white",
            }}
          >
            {errorMessage}
          </FormHelperText>
        )}
      </Box>
    );
  },
  (prev, next) => prev.value === next.value && prev.isEditing === next.isEditing && prev.row.caseMatchId === next.row.caseMatchId,
);
